import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  studentSearchApi,
  studentAssignmentStatusApi,
} from "../../api/AssignmentService";

import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import usePrivateLink from "../config/usePrivateLink";
import { formatToDate } from "../master/FormatToDateTime";
import Pagination from "../components/Pagination";
import AcademicHeader from "../Academic/AcademicHeader";

// =========================================================
// STATUS CONFIG (badge colour + label + summary card)
// =========================================================
const STATUS_META = {
  submitted: {
    label: "Submitted",
    badge: "bg-label-success",
    icon: "ri-checkbox-circle-line",
  },
  pending: {
    label: "Pending",
    badge: "bg-label-warning",
    icon: "ri-time-line",
  },
  not_submitted: {
    label: "Not Submitted",
    badge: "bg-label-danger",
    icon: "ri-close-circle-line",
  },
  no_submission: {
    label: "No Submission",
    badge: "bg-label-secondary",
    icon: "ri-file-forbid-line",
  },
};

const defaultFilters = {
  status: "",
  type: "",
  include_before_joining: "",
  page: 1,
};

export default function StudentAssignmentStatus() {
  const hasAccess = usePrivateLink();
  const [searchParams, setSearchParams] = useSearchParams();

  // student id lives in the URL -> refresh / back button / share link all work
  const studentId = searchParams.get("student_id");

  const [loading, setLoading] = useState(false);

  // ---------- student search ----------
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);

  // ---------- selected student data ----------
  const [student, setStudent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [data, setdata] = useState({});
  const [filters, setFilters] = useState(defaultFilters);

  // =========================================================
  // DEBOUNCED STUDENT SEARCH (name / registration no / phone)
  // =========================================================
  useEffect(() => {
    const text = searchText.trim();

    if (text.length < 2) {
      setSearchResults([]);
      setSearched(false);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await studentSearchApi(
          `search=${encodeURIComponent(text)}`,
        );
        if (cancelled) return;

        if (response?.data?.status) {
          setSearchResults(response?.data?.data?.data || []);
        } else {
          setSearchResults([]);
        }
        setSearched(true);
      } catch (error) {
        if (!cancelled) {
          setSearchResults([]);
          setSearched(true);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchText]);

  // =========================================================
  // FETCH SELECTED STUDENT'S ASSIGNMENTS
  // =========================================================
  const getStudentAssignments = async () => {
    setLoading(true);

    let addUrl = `student_id=${encodeURIComponent(studentId)}`;
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) addUrl += `&${key}=${encodeURIComponent(value)}`;
    });

    try {
      const response = await studentAssignmentStatusApi(addUrl);

      if (response?.data?.status) {
        setStudent(response?.data?.student || null);
        setSummary(response?.data?.summary || null);
        setAssignments(response?.data?.data?.data || []);
        setdata(response?.data?.data || {});
      } else {
        resetStudentData();
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "No data found.",
        );
      }
    } catch (error) {
      resetStudentData();
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const resetStudentData = () => {
    setStudent(null);
    setSummary(null);
    setAssignments([]);
    setdata({});
  };

  useEffect(() => {
    if (studentId) {
      getStudentAssignments();
    } else {
      resetStudentData();
    }
  }, [studentId, filters]);

  // =========================================================
  // HANDLERS
  // =========================================================
  const selectStudent = (item) => {
    setFilters(defaultFilters);
    setSearchParams({ student_id: item.id });
    setSearchText("");
    setSearchResults([]);
    setSearched(false);
  };

  const changeStudent = () => {
    setSearchParams({});
    setFilters(defaultFilters);
  };

  const setStatusFilter = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === status ? "" : status,
      page: 1,
    }));
  };

  // =========================================================
  // HELPERS
  // =========================================================
  const getDueText = (item) => {
    if (item.days_left === null || item.days_left === undefined) return null;

    if (item.days_left === 0) {
      return { text: "Due today", className: "text-warning" };
    }
    if (item.days_left > 0) {
      return {
        text: `${item.days_left} day${item.days_left > 1 ? "s" : ""} left`,
        className: "text-muted",
      };
    }
    const overdue = Math.abs(item.days_left);
    return {
      text: `${overdue} day${overdue > 1 ? "s" : ""} overdue`,
      className: "text-danger",
    };
  };

  const getAssignedVia = (via) => {
    if (via === "personal")
      return <span className="badge bg-label-warning">Personal</span>;
    if (via === "batch")
      return <span className="badge bg-label-info">Batch</span>;
    if (via === "all")
      return <span className="badge bg-label-primary">All Students</span>;
    return null;
  };

  const summaryCards = [
    {
      key: "",
      label: "Total",
      value: summary?.total,
      color: "primary",
      icon: "ri-file-list-3-line",
    },
    {
      key: "submitted",
      label: "Submitted",
      value: summary?.submitted,
      color: "success",
      icon: STATUS_META.submitted.icon,
    },
    {
      key: "pending",
      label: "Pending",
      value: summary?.pending,
      color: "warning",
      icon: STATUS_META.pending.icon,
    },
    {
      key: "not_submitted",
      label: "Not Submitted",
      value: summary?.not_submitted,
      color: "danger",
      icon: STATUS_META.not_submitted.icon,
    },
    {
      key: "no_submission",
      label: "No Submission",
      value: summary?.no_submission,
      color: "secondary",
      icon: STATUS_META.no_submission.icon,
    },
  ];

  // =========================================================
  // RENDER
  // =========================================================
  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-12">
                    {/* ================= HEADER ================= */}
                    <div className="card mb-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h4 className="text-primary mb-1">
                              Student Assignments
                            </h4>
                            <small className="text-muted">
                              Search a student to see all their assignments and
                              submission status.
                            </small>
                          </div>

                          <Link
                            to="/assignment"
                            className="btn btn-outline-secondary"
                          >
                            <i className="ri-arrow-left-line me-1"></i>
                            Back to Assignments
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* ================= SEARCH ================= */}
                    <div className="card mb-4">
                      <div className="card-body">
                        {student ? (
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center fw-bold"
                                style={{ width: 48, height: 48 }}
                              >
                                {(student.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h5 className="mb-0">{student.name}</h5>
                                <small className="text-muted">
                                  Reg No: {student.registration_no || "-"}
                                  {student.phone ? ` • ${student.phone}` : ""}
                                </small>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={changeStudent}
                            >
                              <i className="ri-user-search-line me-1"></i>
                              Change Student
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="row">
                              <div className="col-md-6 form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search student"
                                  value={searchText}
                                  autoFocus
                                  onChange={(e) =>
                                    setSearchText(e.target.value)
                                  }
                                />
                                <label>
                                  Search by Name / Registration No / Phone
                                </label>
                              </div>
                            </div>

                            {searchText.trim().length > 0 &&
                              searchText.trim().length < 2 && (
                                <small className="text-muted d-block mt-2">
                                  Type at least 2 characters...
                                </small>
                              )}

                            {searching && (
                              <small className="text-muted d-block mt-2">
                                Searching...
                              </small>
                            )}

                            {!searching &&
                              searched &&
                              searchResults.length === 0 && (
                                <div className="text-muted mt-3">
                                  No student found.
                                </div>
                              )}

                            {searchResults.length > 0 && (
                              <div className="list-group mt-3 col-md-6 px-0">
                                {searchResults.map((item) => (
                                  <button
                                    type="button"
                                    key={item.id}
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                    onClick={() => selectStudent(item)}
                                  >
                                    <div>
                                      <div className="fw-semibold">
                                        {item.name}
                                      </div>
                                      <small className="text-muted">
                                        Reg No: {item.registration_no || "-"}
                                        {item.phone ? ` • ${item.phone}` : ""}
                                      </small>
                                    </div>
                                    <i className="ri-arrow-right-s-line"></i>
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* ================= SUMMARY + TABLE ================= */}
                    {studentId && student && (
                      <>
                        {/* SUMMARY CARDS (click = filter) */}
                        <div className="row g-3 mb-4">
                          {summaryCards.map((card) => {
                            const active = filters.status === card.key;
                            return (
                              <div className="col" key={card.label}>
                                <div
                                  role="button"
                                  onClick={() => setStatusFilter(card.key)}
                                  className={`card h-100 border ${active
                                    ? `border-${card.color}`
                                    : "border-transparent"
                                    }`}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="card-body d-flex justify-content-between align-items-center py-3">
                                    <div>
                                      <small className="text-muted">
                                        {card.label}
                                      </small>
                                      <h4 className={`mb-0 text-${card.color}`}>
                                        {card.value ?? 0}
                                      </h4>
                                    </div>
                                    <span
                                      className={`badge bg-label-${card.color} rounded-pill p-2`}
                                    >
                                      <i className={`${card.icon} ri-22px`}></i>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* FILTERS */}
                        <div className="card mb-4">
                          <div className="card-body">
                            <div className="row g-3 align-items-center">
                              <div className="col-md-3 form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={filters.type}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      type: e.target.value,
                                      page: 1,
                                    })
                                  }
                                >
                                  <option value="">All Type</option>
                                  <option value="homework">Homework</option>
                                  <option value="task">Task</option>
                                  <option value="project">Project</option>
                                  <option value="activity">Activity</option>
                                </select>
                                <label>Assignment Type</label>
                              </div>

                              <div className="col-md-3 form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={filters.status}
                                  onChange={(e) =>
                                    setFilters({
                                      ...filters,
                                      status: e.target.value,
                                      page: 1,
                                    })
                                  }
                                >
                                  <option value="">All Status</option>
                                  <option value="submitted">Submitted</option>
                                  <option value="pending">Pending</option>
                                  <option value="not_submitted">
                                    Not Submitted
                                  </option>
                                  <option value="no_submission">
                                    No Submission Required
                                  </option>
                                </select>
                                <label>Status</label>
                              </div>

                              <div className="col-md-4">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="includeOld"
                                    checked={!!filters.include_before_joining}
                                    onChange={(e) =>
                                      setFilters({
                                        ...filters,
                                        include_before_joining: e.target.checked
                                          ? 1
                                          : "",
                                        page: 1,
                                      })
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="includeOld"
                                  >
                                    Include assignments given before student
                                    joined
                                  </label>
                                </div>
                              </div>

                              <div className="col-md-2 text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => setFilters(defaultFilters)}
                                >
                                  <i className="ri-refresh-line me-1"></i>
                                  Reset
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* TABLE */}
                        <div className="card">
                          <div className="card-body">
                            <div className="table-responsive">
                              <table className="table table-hover align-middle">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Assignment</th>
                                    <th>Type</th>
                                    <th>Subject</th>
                                    <th>Assigned Via</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th>Submitted On</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {assignments.length > 0 ? (
                                    assignments.map((item, i) => {
                                      const meta =
                                        STATUS_META[item.submission_status];
                                      const due = getDueText(item);

                                      return (
                                        <tr key={item.id}>
                                          <td>{(data?.from || 1) + i}</td>

                                          <td>
                                            <div className="fw-semibold text-primary">
                                              {item.title}
                                            </div>
                                            {item.description && (
                                              <small className="text-muted">
                                                {item.description.length > 50
                                                  ? item.description.substring(
                                                    0,
                                                    50,
                                                  ) + "..."
                                                  : item.description}
                                              </small>
                                            )}
                                          </td>

                                          <td>
                                            <span className="badge bg-label-secondary">
                                              {item.type}
                                            </span>
                                          </td>

                                          <td>
                                            {item.subject?.name ? (
                                              <span className="badge bg-label-info">
                                                {item.subject.name}
                                              </span>
                                            ) : (
                                              "-"
                                            )}
                                          </td>

                                          <td>
                                            {getAssignedVia(item.assigned_via)}
                                          </td>

                                          <td>
                                            <div>
                                              {formatToDate(item.due_date)}
                                            </div>
                                            {due && (
                                              <small className={due.className}>
                                                {due.text}
                                              </small>
                                            )}
                                          </td>

                                          <td>
                                            <span
                                              className={`badge ${meta?.badge || "bg-label-secondary"}`}
                                            >
                                              {item.submission_label}
                                            </span>
                                            {item.is_late && (
                                              <span className="badge bg-label-danger ms-1">
                                                Late
                                              </span>
                                            )}
                                          </td>

                                          <td>
                                            {item.submission?.submitted_at
                                              ? formatToDate(
                                                item.submission.submitted_at,
                                              )
                                              : "-"}
                                          </td>

                                          <td>
                                            {hasAccess("/assignments/view") && (
                                              <Link
                                                to={`/assignments/view?id=${item.id}&type=${item.assigned_via === "personal" ? "student" : item.assigned_via}`}
                                                className="btn btn-sm btn-outline-primary"
                                                title="View"
                                              >
                                                <i className="ri-eye-line"></i>
                                              </Link>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan="9"
                                        className="text-center py-4"
                                      >
                                        No Assignments Found
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>

                              <Pagination
                                data={data}
                                setFilters={setFilters}
                                filters={filters}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* EMPTY STATE */}
                    {!studentId && (
                      <div className="card">
                        <div className="card-body text-center py-5 text-muted">
                          <i className="ri-user-search-line ri-48px d-block mb-2"></i>
                          Search and select a student to see their assignments.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
